import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useParentCredits } from "@/hooks/useParentCredits";
import { toast } from "@/hooks/use-toast";
import { CreditCard, Coins, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";

export default function BuyCredits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ptaId, setPtaId] = useState<string | null>(null);
  const { balance, loading: balanceLoading, refetch } = useParentCredits(ptaId || undefined);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchPTAId();
  }, [user, navigate]);

  const fetchPTAId = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('parents')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();

    if (data) {
      setPtaId(data.pta_id);
    }
  };

  const creditPackages = [
    {
      credits: 5,
      price: 5,
      discount: 0,
      popular: false
    },
    {
      credits: 10,
      price: 9,
      discount: 10,
      popular: true
    },
    {
      credits: 20,
      price: 16,
      discount: 20,
      popular: false
    }
  ];

  const handlePurchase = async (credits: number, price: number) => {
    if (!user || !ptaId) {
      toast({
        title: "Error",
        description: "Please make sure you're registered as a parent",
        variant: "destructive"
      });
      return;
    }

    setPurchasing(true);
    
    // TODO: Integrate with Stripe when keys are added
    toast({
      title: "Payment System Pending",
      description: "Stripe integration will be configured soon. Contact your PTA admin to enable credit purchases.",
    });

    setPurchasing(false);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Buy Credits</h1>
            <p className="text-muted-foreground">
              Purchase credits to list items in the marketplace
            </p>
          </div>

          {balanceLoading ? (
            <Card>
              <CardContent className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coins className="h-5 w-5" />
                    Your Credit Balance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-4xl font-bold text-primary">
                        {balance?.credits_available || 0}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Available Credits
                      </div>
                    </div>
                    {(balance?.credits_pending || 0) > 0 && (
                      <div className="text-right">
                        <div className="text-2xl font-semibold text-orange-600">
                          {balance.credits_pending}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Pending Approval
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Credits are used to list items for sale in the marketplace. Each listing costs 1 credit (or as set by your PTA). Credits purchased support your PTA!
                </AlertDescription>
              </Alert>

              <div className="grid md:grid-cols-3 gap-6">
                {creditPackages.map((pkg) => (
                  <Card 
                    key={pkg.credits} 
                    className={pkg.popular ? "border-primary border-2 shadow-lg" : ""}
                  >
                    {pkg.popular && (
                      <div className="bg-primary text-primary-foreground text-center py-1 text-sm font-semibold">
                        Most Popular
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="text-center">
                        <div className="text-4xl font-bold text-primary mb-2">
                          {pkg.credits}
                        </div>
                        <div className="text-lg font-normal text-muted-foreground">
                          Credits
                        </div>
                      </CardTitle>
                      <CardDescription className="text-center">
                        <div className="text-3xl font-bold text-foreground">
                          £{pkg.price}
                        </div>
                        {pkg.discount > 0 && (
                          <Badge variant="secondary" className="mt-2">
                            Save {pkg.discount}%
                          </Badge>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        className="w-full" 
                        onClick={() => handlePurchase(pkg.credits, pkg.price)}
                        disabled={purchasing}
                        variant={pkg.popular ? "default" : "outline"}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        {purchasing ? "Processing..." : "Buy Now"}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">1.</span>
                    <p>Purchase credits using your debit/credit card via secure Stripe payment</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">2.</span>
                    <p>Credits are added to your account instantly</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">3.</span>
                    <p>Use credits to list items for sale in the marketplace</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">4.</span>
                    <p>If your listing is rejected, credits are refunded automatically</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">5.</span>
                    <p>All credit purchases go directly to your PTA to support school activities</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
